import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import RelicSprite from '../shared/RelicSprite';
import {
  COLORS,
  RARITY_CARD,
  accentButton,
  bodyFont,
  em,
  headingFont,
  hoverTransition,
  mythicTextStyle,
} from '../shared/theme';
import { guildLabelFor, rarityColorFor } from 'utils/relicVault/catalog';
import { relicCardGlow } from 'utils/relicVault/colorUtils';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/** A relic offered in one of the store's slots (.card.rarity-<key>). */
const RelicCard = ({ catalog, relic, owned, canAfford, width, compact, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const disabled = owned || !canAfford;
  const rarityColor = rarityColorFor(catalog, relic);
  const rarity = RARITY_CARD[relic.rarity] || RARITY_CARD.common;
  const lifted = hovered && !owned;
  const badgeSize = compact ? 96 : 118;

  return (
    <View
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={[
        styles.card,
        { width, borderColor: owned ? 'rgba(255,255,255,0.14)' : lifted ? rarity.borderHover : rarity.border },
        lifted && styles.cardLifted,
        webOnly({
          backgroundImage: lifted
            ? 'linear-gradient(165deg, rgba(92,84,74,0.98) 0%, rgba(42,38,32,0.98) 55%, rgba(18,16,13,0.99) 100%)'
            : 'linear-gradient(165deg, rgba(58,54,50,0.98) 0%, rgba(20,18,16,0.98) 55%, rgba(8,7,6,0.99) 100%)',
          boxShadow: owned
            ? 'none'
            : lifted
            ? `${rarity.shadowHover}, 0 0 24px rgba(255,255,255,0.14), 0 10px 24px rgba(0,0,0,0.4)`
            : rarity.shadow,
        }),
        owned && styles.cardOwned,
        webOnly(owned ? { filter: 'grayscale(0.85) brightness(0.7)' } : null),
        hoverTransition(),
      ]}>
      {owned && (
        <View style={[styles.ownedBadge, webOnly({ backgroundImage: 'linear-gradient(135deg, #9691AC, #6B6580)' })]}>
          <Text style={styles.ownedBadgeText}>{'✓'} Added to Vault</Text>
        </View>
      )}

      <Text
        style={[
          styles.rarityTag,
          { color: rarityColor },
          relic.rarity === 'mythic' && mythicTextStyle,
        ]}>
        {catalog.tierByKey.get(relic.rarity)?.label}
      </Text>

      <View
        style={[
          styles.iconBadge,
          { width: badgeSize, height: badgeSize, borderRadius: badgeSize / 2 },
          IS_WEB
            ? { backgroundImage: relicCardGlow(rarityColor) }
            : { backgroundColor: `${rarityColor}22` },
        ]}>
        {/* The design scales the 160px icon to 57.5%. */}
        <RelicSprite relic={relic} grid={catalog.sheetGrid} size={92} />
      </View>

      <Text style={styles.itemTag} numberOfLines={1}>
        {guildLabelFor(catalog, relic)}
      </Text>
      <Text style={styles.itemName} numberOfLines={2}>
        {relic.name}
      </Text>

      <TouchableOpacity
        activeOpacity={0.9}
        disabled={disabled}
        onPress={() => onSelect(relic.relicId)}
        accessibilityRole="button"
        accessibilityLabel={owned ? `${relic.name}, added to vault` : `Buy ${relic.name} for ${relic.price}`}
        style={[
          styles.buyBtn,
          owned ? styles.buyBtnOwned : disabled ? styles.buyBtnDisabled : accentButton(),
        ]}>
        <Text
          style={[
            styles.buyBtnText,
            owned && { color: '#9891B0' },
            !owned && disabled && { color: '#8B85A3' },
          ]}>
          {owned ? 'Added to Vault' : `Buy ${relic.price}`}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    backgroundColor: '#1A1815',
    borderWidth: 1.5,
    borderRadius: 18,
    paddingTop: 18,
    paddingHorizontal: 16,
    paddingBottom: 16,
    alignItems: 'center',
  },
  cardLifted: {
    transform: [{ translateY: -3 }],
  },
  cardOwned: {
    opacity: 0.6,
  },
  ownedBadge: {
    position: 'absolute',
    top: -9,
    right: 12,
    zIndex: 2,
    backgroundColor: '#817C96',
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
  },
  ownedBadgeText: {
    ...headingFont('800'),
    fontSize: 10,
    color: COLORS.text,
  },
  rarityTag: {
    ...bodyFont('800'),
    fontSize: 10.5,
    letterSpacing: em(0.08, 10.5),
    textTransform: 'uppercase',
  },
  iconBadge: {
    marginTop: 12,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  itemTag: {
    ...bodyFont('600'),
    fontSize: 11.5,
    color: COLORS.muted,
    marginTop: 4,
    textAlign: 'center',
  },
  itemName: {
    ...headingFont('700'),
    fontSize: 15,
    lineHeight: 19.5,
    minHeight: 39,
    marginTop: 12,
    color: COLORS.text,
    textAlign: 'center',
  },
  buyBtn: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 12,
    paddingHorizontal: 12,
    marginTop: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buyBtnOwned: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  buyBtnDisabled: {
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  buyBtnText: {
    ...headingFont('700'),
    fontSize: 12.5,
    letterSpacing: em(0.03, 12.5),
    textTransform: 'uppercase',
    color: '#FFFFFF',
    textAlign: 'center',
  },
});

export default React.memo(RelicCard);
